import { useState, useEffect } from 'react';

import type { ClientGoalDto, GetCriticalElementGoalsParams } from 'api/generated/schemas';

import { getClientCriticalElements } from 'api/generated/critical-elements/critical-elements';
import { getCriticalElementGoals } from 'api/generated/critical-element-goals/critical-element-goals';

import { useCurrentUser } from 'state/current-user/current-user.thunk';

import type { CriticalElement } from 'containers/admin/clients/client/tabs-components/goals-tab/types';

interface CurrentUserShape {
  data?: { oktaUserId?: string };
}

interface UseCriticalElementsResult {
  criticalElements: Array<CriticalElement & { goals: ClientGoalDto[] }>;
  loading: boolean;
  error: string | null;
  fetchCriticalElements: () => Promise<void>;
}

const useCriticalElements = (
  clientId: string | number | undefined,
  year: string | number | undefined
): UseCriticalElementsResult => {
  const [criticalElements, setCriticalElements] = useState<
    Array<CriticalElement & { goals: ClientGoalDto[] }>
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const currentUser = useCurrentUser() as CurrentUserShape;

  const fetchCriticalElementsWithGoals = async (): Promise<void> => {
    if (!clientId || !currentUser?.data?.oktaUserId) return;
    const userId = currentUser.data.oktaUserId;

    try {
      setLoading(true);

      const response = await getClientCriticalElements(userId, Number(clientId), {
        ...(year !== undefined && year !== ''
          ? { 'enteringClassYear.equals': Number(year) }
          : {}),
        sort: ['createdDate,desc'],
      });

      if (!response || !Array.isArray(response)) {
        setCriticalElements([]);

        return;
      }

      const elements = response;

      const goalsPromises = elements.map((element) =>
        getCriticalElementGoals(userId, Number(element.id), {
          ...(year !== undefined && year !== ''
            ? { 'enteringClassYear.equals': Number(year) }
            : {}),
        } as GetCriticalElementGoalsParams)
          .then((goals) => ({
            elementId: element.id,
            goals: goals || [],
          }))
          .catch((err) => {
            console.error(
              `Error fetching goals for element ${element.id}:`,
              err
            );

            return { elementId: element.id, goals: [] as ClientGoalDto[] };
          })
      );

      const goalsResults = await Promise.all(goalsPromises);

      const goalsMap = goalsResults.reduce<
        Record<string, ClientGoalDto[]>
      >((map, result) => {
        map[String(result.elementId)] = result.goals;

        return map;
      }, {});

      const elementsWithGoals = elements.map((element) => ({
        ...element,
        goals: goalsMap[String(element.id)] || [],
      }));

      setCriticalElements(
        // TODO(ts-migration): generated CE list vs goals-tab CriticalElement — migration-debt.md (2026-05-07 CE migration)
        elementsWithGoals as unknown as Array<
          CriticalElement & { goals: ClientGoalDto[] }
        >
      );
      setError(null);
    } catch (err) {
      console.error('Error fetching critical elements:', err);
      setError(
        (err as Error)?.message || 'Failed to fetch critical elements'
      );
      setCriticalElements([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCriticalElementsWithGoals();
  }, [clientId, year, currentUser?.data?.oktaUserId]);

  return {
    criticalElements,
    loading,
    error,
    fetchCriticalElements: fetchCriticalElementsWithGoals,
  };
};

export default useCriticalElements;
